import { z } from "zod";
//#region src/reconcile-refusal.ts
/**
* Every reason the operator refuses to deploy one fixture service or worker.
*
* - `invalid-machine-type` — the resolved or declared machine type is not in this DP's enabled
*   catalog. Fail-closed: there is NO fallback to the default pool, because a silent fallback is
*   how a fixture ends up on hardware nobody chose (the `machineType` no-op this program removes).
* - `resource-ceiling`     — the effective vector (declared × the effective size multiplier)
*   exceeds the RESOLVED machine type's derived allocatable ceilings. This is F-4: today such a pod
*   is admitted, goes Pending forever, autoscaling adds more identical nodes to the cap and stops,
*   and no platform layer names the manifest as the cause.
* - `class-unavailable`    — no machine family in the declared class's ladder is enabled in this
*   DP's region. DISTINCT from `resource-ceiling` because the remedy has a different OWNER: this
*   one is a platform-operator action (enable a machine type, or move the tenant), not an app
*   change.
* - `class-vector-exceeded` — families are present but no shape in the class satisfies the ask.
*   The app-side twin of `class-unavailable`.
* - `accelerator-unavailable` — an `accelerator` pin matches no enabled machine type here.
* - `arch-unavailable`     — the `arch` trait matches no family in the class here.
* - `invalid-compute-ask`  — the declaration is structurally unusable (a bad `gpuCount`, an
*   `accelerator` on a non-GPU class, an unknown class or arch). Should be unreachable for a
*   manifest that passed a publish door, and is therefore the code that tells you a manifest reached
*   reconcile by a route that skipped one.
*
* ⭐ THE LAST FIVE COVER `MachineTypeRefusalReason`'s SIX — a SURJECTION, not a bijection (D60(b),
* corrected: this said "1:1", which briefs the stage-3 author WRONG). Four line up by name
* (`class-unavailable-in-region`→`class-unavailable`, `vector-exceeds-class`→`class-vector-exceeded`,
* `accelerator-unavailable`, `arch-unavailable`), and **`unknown-class` AND `invalid-ask` BOTH
* collapse into `invalid-compute-ask`** — which the bullet above already implies ("an unknown class
* or arch"). ⇒ The operator-side `Record<MachineTypeRefusalReason, ReconcileRefusalCode>` (D38.3) has
* SIX keys and five distinct values; a reader expecting a bijection will look for a sixth code that
* does not exist. The first two (`invalid-machine-type`, `resource-ceiling`) are OPERATOR-ONLY
* POST-RESOLUTION codes — the resolver has already succeeded when they fire, so they are correctly
* twinless (D38.3).
*
* ⛔ THE TRANSLATION MAP IS **NOT** IN THIS PACKAGE (D38.3). `MachineTypeRefusalReason →
* ReconcileRefusalCode` lives OPERATOR-side as a compile-time-exhaustive
* `Record<MachineTypeRefusalReason, ReconcileRefusalCode>`, so a widened resolver reason is a BUILD
* ERROR there rather than a cast. Do not add the map here, and do not add a test asserting the
* mapping is total — that test belongs to the package that owns the map, and duplicating it here
* would let the real one be deleted while a green copy remained. This package's only obligation is
* that `MachineTypeRefusalReason` and `MachineTypeResolutionNote` are EXPORTED.
*/
const RECONCILE_REFUSAL_CODES = [
	"invalid-machine-type",
	"resource-ceiling",
	"class-unavailable",
	"class-vector-exceeded",
	"accelerator-unavailable",
	"arch-unavailable",
	"invalid-compute-ask"
];
const reconcileRefusalCodeSchema = z.enum(RECONCILE_REFUSAL_CODES);
/** True iff `c` is a known {@link ReconcileRefusalCode}. */
function isReconcileRefusalCode(c) {
	return typeof c === "string" && RECONCILE_REFUSAL_CODES.includes(c);
}
/**
* Whether the remedy is the APP TEAM's or the PLATFORM OPERATOR's.
*
* ⭐ This is the field that decides who gets paged, and it is the reason the taxonomy has seven
* members instead of two. Without it, "this data plane has no memory machine types enabled" and
* "your fixture asks for more RAM than any shape in your class has" arrive as the same red badge on
* the same screen, and an app owner spends an afternoon shrinking a vector that was never the
* problem. Same conflation `WITHHOLD_SCOPE` exists to prevent, one layer down.
*/
const RECONCILE_REFUSAL_OWNERS = ["app", "platform-operator"];
/**
* ✅ **REINSTATED BY D50(a) — it now has a real door.**
*
* ⚠️ THIS SYMBOL WAS DELETED AND IS BACK, AND THE HISTORY IS THE POINT. D44 removed it because it had
* zero consumers: the owner was only ever DERIVED from {@link RECONCILE_REFUSAL_OWNER}, never parsed
* off a wire, so a zod enum was the same zero-call-site defect D41(b) deleted
* `reconcileRefusalSchema` for — one symbol over, in the same file.
*
* ⭐ THEN THE ROUND-1 FIX PASS CREATED THE CONSUMER. WS-20 §6.5a stamps `owner` onto a `.strict()`
* wire section, and its consumers include **MCP agents receiving proxied JSON with no access to the
* TypeScript maps** — for them owner (and remedy) ON THE WIRE is the entire point, and a `.strict()`
* object needs a schema for the field. The premise moved, so the ruling moved with it.
*
* ⇒ **Security impact nil:** `owner` is CP-stamped from the total map above, never DP-asserted, so
* this schema validates a value the CP itself just produced. It is a wire-shape declaration, not a
* trust boundary.
*/
const reconcileRefusalOwnerSchema = z.enum(RECONCILE_REFUSAL_OWNERS);
/** `code → owner`. Total by construction; a new code without an owner is a TYPE error. */
const RECONCILE_REFUSAL_OWNER = {
	"invalid-machine-type": "platform-operator",
	"resource-ceiling": "app",
	"class-unavailable": "platform-operator",
	"class-vector-exceeded": "app",
	"accelerator-unavailable": "platform-operator",
	"arch-unavailable": "platform-operator",
	"invalid-compute-ask": "app"
};
/**
* `code → remedy` — the human next action, one per code, derived at the read boundary rather than
* carried through the reconcile loop (same discipline as `WITHHOLD_REMEDY`: the sink runs on a ~30s
* loop over every fixture and must not build strings it does not need).
*
* These are INSTRUCTIONS, not restatements of the fault — the `detail` on the refusal already says
* what is wrong. Each `platform-operator` remedy names an action an app owner CANNOT take, which is
* what {@link RECONCILE_REFUSAL_OWNER} is for.
*/
const RECONCILE_REFUSAL_REMEDY = {
	"invalid-machine-type": "Platform operator: the machine type this fixture resolved to is not enabled on this data plane. Enable it under Platform → Data planes → Machine types, or enable another family in the fixture’s class. This is not a fault in the app.",
	"resource-ceiling": "The effective resource request (the declared vector times the tenant size multiplier) is larger than any single node in this class can allocate. Lower the request, split the work across replicas, or declare a class whose shapes are big enough.",
	"class-unavailable": "Platform operator: no machine family in this fixture’s compute class is enabled in this data plane’s region — some regions carry no memory or GPU families at all. Enable one of the families named in `detail`, or place the tenant on a data plane that has them.",
	"class-vector-exceeded": "No machine type in this fixture’s compute class satisfies its effective request on this data plane. Lower the request, or declare a class with larger shapes (`M` is the only class that serves more than 8 GiB per vCPU).",
	"accelerator-unavailable": "Platform operator: the accelerator this fixture pins is not on any machine type enabled here. Enable a machine type carrying it, or ask the app team to drop the pin and let the platform choose the accelerator.",
	"arch-unavailable": "Platform operator: no machine family in this fixture’s class offers the requested CPU architecture on this data plane. Enable an arm64 family (only C4A and N4A offer it on GCP), or ask the app team to drop the architecture trait.",
	"invalid-compute-ask": "This fixture’s compute declaration is not valid — it should have been refused at publish, so it most likely reached the platform by a route that skipped validation. Re-publish the app through the app pipeline; `detail` names the field."
};
//#endregion
export { RECONCILE_REFUSAL_CODES, RECONCILE_REFUSAL_OWNER, RECONCILE_REFUSAL_OWNERS, RECONCILE_REFUSAL_REMEDY, isReconcileRefusalCode, reconcileRefusalCodeSchema, reconcileRefusalOwnerSchema };

//# sourceMappingURL=reconcile-refusal.js.map